import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useParams from 'core/hooks/useParams'
import { getResmgrRoleConfig, updateHostRole } from '../actions'
import ModalForm from 'core/elements/modal/ModalForm'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Text from 'core/elements/Text'
import SelectableCard from 'core/components/SelectableCard'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import { isNilOrEmpty } from 'utils/fp'
import Alert from 'core/components/Alert'
import Badge from 'core/elements/badge'

const useStyles = makeStyles<Theme>((theme) => ({
  pairs: {
    display: 'grid',
    gridTemplateColumns: 'max-content auto',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  fields: {
    display: 'grid',
    gap: 8,
  },
  card: {
    padding: '12px 20px',
    alignItems: 'center',
  },
  profileCards: {
    display: 'grid',
    gap: 16,
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
  },
  smallText: {
    fontSize: 12,
  },
}))

interface Props {
  rows: any[]
  onClose: () => void
}

export default function ConfigureGpuHostModal({ rows: [host], onClose }: Props) {
  const classes = useStyles()
  const defaultParams = {
    profiles: [],
    profileObject: null,
  }
  const [submitting, setSubmitting] = useState(false)
  const [loadingProfiles, setLoadingProfiles] = useState(true)
  const [errorLoadingConfig, setErrorLoadingConfig] = useState(false)
  const [error, setError] = useState(null)
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const [role, setRole] = useState(null)

  const toggleProfile = useCallback(
    (profile) => {
      updateParams({ profiles: [profile] })
    },
    [params],
  )

  useEffect(() => {
    const getConfiguration = async () => {
      try {
        const response: any = await getResmgrRoleConfig({ host, role: 'pf9-ostackhost-neutron' })
        if (response) setRole(response)
        if (response?.enabled_mdev_types) {
          updateParams({ profiles: [response.enabled_mdev_types] })
        }
        setLoadingProfiles(false)
      } catch (e) {
        setErrorLoadingConfig(true)
      }
    }
    getConfiguration()
  }, [host])

  const submitForm = useCallback(async () => {
    if (host.gpuMode !== 'vGPU') {
      handleClose()
      return
    }
    setSubmitting(true)
    const body = {
      ...role,
      enabled_mdev_types: params.profiles[0],
      mdev_config: params.profileObject
        ? JSON.stringify({
            name: `mdev_${params.profiles[0]}`,
            max_instances: params.profileObject?.['Max Instances'],
          })
        : '',
    }

    try {
      await updateHostRole({ hostId: host.id, role: 'pf9-ostackhost-neutron', body })
    } catch (e) {
      setError({ message: 'Configuration failed to update' })
      return
    } finally {
      handleClose()
      setSubmitting(false)
    }
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    setLoadingProfiles(true)
    setErrorLoadingConfig(false)
    onClose()
  }

  const existingProfile = useMemo(() => {
    return host?.allGpuProfiles?.find((profile) => profile.profileName === role?.enabled_mdev_types)
      ?.name
  }, [role])

  return (
    <ModalForm
      title={`Configure GPU Host Model`}
      onSubmit={submitForm}
      onClose={handleClose}
      loading={loadingProfiles}
      submitting={submitting}
      error={error}
      submitTitle={`Save Changes`}
      disableSubmit={isNilOrEmpty(role) || submitting}
      info={<Badge variant="warning" text="Beta" />}
      open
    >
      {isNilOrEmpty(role) && (
        <>
          <Alert variant="error">{'Required GPU role not found'}</Alert>
          <br />
        </>
      )}
      <div className={classes.pairs}>
        <Text variant="body2">Host Name:</Text>
        <Text variant="caption1">{host.name}</Text>
        <Text variant="body2">GPU Model:</Text>
        <Text variant="caption1">{host.gpuModels?.join(', ')}</Text>
        <Text variant="body2">Device ID:</Text>
        <Text variant="caption1">{host.deviceIds?.join(', ')}</Text>
        <Text variant="body2">GPU Mode:</Text>
        <Text variant="caption1">{host.gpuMode}</Text>
        <Text variant="body2">vGPU Profile:</Text>
        <Text variant="caption1">{existingProfile}</Text>
      </div>
      {host.gpuMode === 'Passthrough' && (
        <div>
          <Text variant="body2">No configurations required for passthrough GPU mode.</Text>
        </div>
      )}
      {host.gpuMode === 'vGPU' && (
        <div className={classes.fields}>
          <Text variant="subtitle2">vGPU Profiles</Text>
          {errorLoadingConfig && <ErrorMessage>Error loading configured profiles</ErrorMessage>}
          <Text variant="body2">
            Select a profile that this host supports. This profile defines how the GPU resources are
            split for use in virtual machines.
          </Text>
          <div className={classes.profileCards}>
            {host?.allGpuProfiles?.map((gpuProfile) => (
              <SelectableCard
                key={gpuProfile.profileName}
                id={gpuProfile.profileName}
                onClick={(val) => {
                  // toggleProfile(val)
                  updateParams({ profiles: [val], profileObject: gpuProfile })
                }}
                active={params.profiles.includes(gpuProfile.profileName)}
                className={classes.card}
                withCustomBody
                showRadioButton
              >
                <Text variant="caption1">{gpuProfile.name}</Text>
                <Text variant="body2" className={classes.smallText}>
                  {`${gpuProfile['Max Instances']} slices (${(
                    gpuProfile['Memory'].replace('M', '') / 1024
                  ).toFixed(1)} GiB Each)`}
                </Text>
              </SelectableCard>
            ))}
          </div>
        </div>
      )}
    </ModalForm>
  )
}
